import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadFile, deleteFile, getFilePathFromUrl } from '@/lib/storage';

interface StoredImage {
  id: string;
  image_url: string;
  caption: string | null;
  created_at: string;
  storage_path?: string;
}

interface ProjectImagesProps {
  projectId: string;
}

export default function ProjectImages({ projectId }: ProjectImagesProps) {
  const [images, setImages] = useState<StoredImage[]>([]);
  const [lightbox, setLightbox] = useState<{ url: string; caption: string; index: number } | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Lightbox Zoom/Pan State
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Fetch images from database on mount
  useEffect(() => {
    async function fetchImages() {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('images')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Resimler yüklenirken bir hata oluştu');
        setLoading(false);
        return;
      }

      setImages(data || []);
      setLoading(false);
    }

    fetchImages();
  }, [projectId]);

  const handleUpload = async (fileList: FileList) => {
    const imageFiles = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      setError('Lütfen sadece resim dosyaları yükleyin (PNG, JPG, WEBP)');
      return;
    }

    setUploading(true);
    setError('');

    for (const file of imageFiles) {
      // Upload to storage
      const { url, error: uploadError } = await uploadFile(file, projectId, 'images');

      if (uploadError || !url) {
        const errorMsg = uploadError?.message || 'Bilinmeyen hata';
        if (errorMsg.includes('bucket') || errorMsg.includes('Bucket')) {
          setError(`Storage bucket bulunamadı. Lütfen admin ile iletişime geçin.`);
        } else if (errorMsg.includes('size') || errorMsg.includes('Size')) {
          setError(`"${file.name}" çok büyük. Maksimum dosya boyutu aşıldı.`);
        } else if (errorMsg.includes('auth') || errorMsg.includes('Auth')) {
          setError(`Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.`);
        } else {
          setError(`"${file.name}" yüklenirken hata: ${errorMsg}`);
        }
        setUploading(false);
        return;
      }

      // Insert into database
      const { data: inserted, error: dbError } = await supabase
        .from('images')
        .insert({
          project_id: projectId,
          image_url: url,
          caption: file.name.replace(/\.[^/.]+$/, ''),
        })
        .select()
        .single();

      if (dbError) {
        // Clean up uploaded file if DB insert fails
        const path = getFilePathFromUrl(url);
        if (path) await deleteFile(path);
        setError(`"${file.name}" kaydedilirken hata oluştu. Lütfen tekrar deneyin.`);
        setUploading(false);
        return;
      }

      if (inserted) {
        setImages((prev) => [inserted, ...prev]);
      }
    }

    setUploading(false);
    setShowUpload(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    handleUpload(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDelete = async (image: StoredImage) => {
    // Delete from storage first — if this fails, abort to avoid orphaned DB records
    const path = image.storage_path || getFilePathFromUrl(image.image_url);
    if (path) {
      const { error: storageError } = await deleteFile(path);
      if (storageError) {
        setError('Resim storage\'dan silinirken bir hata oluştu. Lütfen tekrar deneyin.');
        return;
      }
    }

    // Only delete from database after storage is confirmed deleted
    const { error: dbError } = await supabase
      .from('images')
      .delete()
      .eq('id', image.id);

    if (dbError) {
      setError('Resim veritabanından silinirken bir hata oluştu');
      return;
    }

    setImages((prev) => prev.filter((img) => img.id !== image.id));
  };

  const openLightbox = (index: number) => {
    const img = images[index];
    if (img) {
      setLightbox({ url: img.image_url, caption: img.caption || '', index });
    }
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (!lightbox) return;
    const newIndex = direction === 'next'
      ? (lightbox.index + 1) % images.length
      : (lightbox.index - 1 + images.length) % images.length;
    const img = images[newIndex];
    if (img) {
      setLightbox({ url: img.image_url, caption: img.caption || '', index: newIndex });
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    const delta = e.deltaY;
    const zoomIn = delta < 0;
    const factor = zoomIn ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * factor, 1), 5);
    setScale(newScale);
    
    // Reset position if zoomed out to 1
    if (newScale === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for pinch-to-zoom
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setInitialDistance(getDistance(e.touches));
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialDistance !== null) {
      const currentDistance = getDistance(e.touches);
      const factor = currentDistance / initialDistance;
      const newScale = Math.min(Math.max(scale * factor, 1), 5);
      setScale(newScale);
      setInitialDistance(currentDistance);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setInitialDistance(null);
    setIsDragging(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Resimler</h2>
          <p className="text-xs text-stone-400 mt-0.5">{images.length} resim</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          disabled={uploading}
          className="px-3 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          <i className="ri-upload-cloud-line"></i>
          {uploading ? 'Yükleniyor...' : 'Resim Yükle'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Upload Area */}
      {showUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver ? 'border-amber-500 bg-amber-50' : 'border-stone-300 bg-white'
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
            <i className="ri-image-add-line text-stone-400 text-xl"></i>
          </div>
          <p className="text-sm text-stone-600 font-medium">Resimleri buraya sürükleyin</p>
          <p className="text-xs text-stone-400 mt-1">PNG, JPG, WEBP desteklenir</p>
          <label className="mt-3 inline-block px-4 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors cursor-pointer whitespace-nowrap">
            Resim Seç
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowUpload(false)}
            className="block mx-auto mt-3 text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
          >
            İptal
          </button>
        </div>
      )}

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
            <i className="ri-image-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-400 text-sm">Henüz resim bulunmuyor</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="group relative bg-white rounded-xl border border-stone-200 overflow-hidden cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={image.image_url}
                  alt={image.caption || 'Proje resmi'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <p className="text-white text-xs font-medium truncate">{image.caption}</p>
                </div>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          <div 
            className="max-w-4xl max-h-[80vh] px-4 flex items-center justify-center overflow-hidden cursor-move" 
            onClick={(e) => e.stopPropagation()}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              ref={imgRef}
              src={lightbox.url}
              alt={lightbox.caption}
              className="max-w-full max-h-[70vh] object-contain rounded-lg transition-transform duration-200 ease-out select-none pointer-events-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              }}
            />
          </div>
          <div className="absolute bottom-10 left-0 right-0 pointer-events-none">
            {lightbox.caption && (
              <p className="text-white text-center text-sm mt-3">{lightbox.caption}</p>
            )}
            <p className="text-white/50 text-center text-xs mt-1">
              {lightbox.index + 1} / {images.length}
            </p>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>

          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}